import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, Textarea } from '@/components/ui';
import { createCompany, updateCompany } from '@/data/client';
import { useMembers } from '@/data/hooks';
import type { Company, CompanyInput } from '@/data/types';
import { LIFECYCLE_OPTIONS } from './LifecycleBadge';
import { FormError, Modal } from './Modal';

/* Create (no `company`) or edit. A duplicate domain comes back as a 409 naming
   the existing company — shown as the form error rather than silently merged. */
export function CompanyFormModal({ company, onClose }: { company?: Company; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { ownerOptions } = useMembers();
  const [f, setF] = useState<CompanyInput>({
    name: company?.name ?? '', website: company?.website ?? '', domain: company?.domain ?? '',
    phone: company?.phone ?? '', city: company?.city ?? '', state: company?.state ?? '',
    lifecycle: company?.lifecycle ?? 'prospect', ownerUserId: company?.ownerUserId ?? undefined,
    address: company?.address ?? '', industry: company?.industry ?? '', services: company?.services ?? '', serviceAreas: company?.serviceAreas ?? '',
  });
  const set = (k: keyof CompanyInput) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => (company ? updateCompany(company.id, f) : createCompany(f)),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      void qc.invalidateQueries({ queryKey: ['company', saved.id] });
      onClose();
      if (!company) navigate(`/companies/${saved.id}`);
    },
  });

  return (
    <Modal
      title={company ? 'Edit company' : 'New company'}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || !f.name?.trim()}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </>}
    >
      <form className="crm-form" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Input label="Name" required autoFocus value={f.name ?? ''} onChange={set('name')} />
        <div className="crm-grid2">
          <Input label="Website" placeholder="acme.com" value={f.website ?? ''} onChange={set('website')} />
          <Input label="Email domain" placeholder="From website if blank" hint="Leads from this domain match this company." value={f.domain ?? ''} onChange={set('domain')} />
          <Input label="Phone" value={f.phone ?? ''} onChange={set('phone')} />
          <Select label="Lifecycle" options={LIFECYCLE_OPTIONS} value={f.lifecycle} onChange={set('lifecycle')} />
          <Input label="City" value={f.city ?? ''} onChange={set('city')} />
          <Input label="State" value={f.state ?? ''} onChange={set('state')} />
          <Input label="Address" value={f.address ?? ''} onChange={set('address')} />
          <Input label="Industry" placeholder="e.g. HVAC contractor" value={f.industry ?? ''} onChange={set('industry')} />
        </div>
        <div className="crm-grid2">
          <Textarea label="Services" rows={2} value={f.services ?? ''} onChange={set('services')} />
          <Textarea label="Service areas" rows={2} value={f.serviceAreas ?? ''} onChange={set('serviceAreas')} />
        </div>
        <Select label="Owner" options={ownerOptions} placeholder="Choose owner" value={f.ownerUserId ?? ''} onChange={set('ownerUserId')} />
        <FormError error={save.error} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
